import { runAz } from "./az.js";
import type { InventoryRow } from "./inventory-summary.js";
import { summarizeInventory, renderInventoryNote } from "./inventory-summary.js";

/**
 * Discriminated so a probe FAILURE stays distinguishable from an EMPTY
 * subscription. A failure must never masquerade as "no resources" — that would
 * let the intake claim it scanned when it did not. Mirrors model-catalog.ts.
 */
export type InventoryResult =
  | { ok: true; rows: InventoryRow[] }
  | { ok: false; error: "denied" | "unavailable" | "malformed" };

/** The raw az row — only four fields are read; the rest is intentionally dropped. */
interface AzResourceEntry {
  type?: string;
  resourceGroup?: string;
  location?: string;
  createdTime?: string | null;
}

/**
 * List the subscription's resources, normalized to the minimal InventoryRow.
 *
 * THE BOUNDARY DROP: only type/resourceGroup/location/createdTime survive. Names,
 * systemData.createdBy (a raw email), and tags never leave this function, so no
 * founder- or subscription-authored free text can reach the note. Never throws;
 * every failure is a typed result. `denied` (403) is kept distinct from
 * `unavailable` (transient) — only the former is a stable property of the account.
 */
export async function listSubscriptionResources(subscriptionId: string): Promise<InventoryResult> {
  let out: string;
  try {
    out = await runAz(["resource", "list", "--subscription", subscriptionId, "--output", "json"]);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const denied = /AuthorizationFailed|Forbidden|\b403\b|does not have authorization/i.test(msg);
    return { ok: false, error: denied ? "denied" : "unavailable" };
  }

  let raw: unknown;
  try {
    raw = JSON.parse(out);
  } catch {
    return { ok: false, error: "malformed" };
  }
  if (!Array.isArray(raw) || raw.some((e) => typeof e !== "object" || e === null)) {
    return { ok: false, error: "malformed" };
  }

  const rows: InventoryRow[] = (raw as AzResourceEntry[]).map((e) => ({
    type: e.type ?? "",
    resourceGroup: e.resourceGroup ?? "",
    location: e.location ?? "",
    createdTime: e.createdTime ?? null,
  }));
  return { ok: true, rows };
}

/** Roughly the model cascade's overlap window; the scan runs beside it, so this is
 *  a liveness backstop, not a selector. */
const DEFAULT_DEADLINE_MS = 25_000;

/**
 * Produce the `subscription_inventory_note` for the intake deploy — or nothing.
 *
 * NEVER throws and NEVER blocks the intake: any failure, timeout, or unforeseen
 * error yields `{}` (no `note` key), so the caller omits the field override and the
 * persona frontmatter default applies. On success the note reflects the scan.
 *
 * `scanImpl` is an injected test seam (defaults to the real `listSubscriptionResources`).
 * The scan promise is `.catch`-guarded up front so that even a contract-violating
 * late rejection after the deadline can never surface as an unhandledRejection in
 * the founder's terminal (C2).
 */
export async function resolveSubscriptionInventory(args: {
  subscriptionId: string;
  installResourceGroup: string;
  now?: () => number;
  deadlineMs?: number;
  onNarrate?: (msg: string) => void;
  scanImpl?: (subscriptionId: string) => Promise<InventoryResult>;
}): Promise<{ note?: string }> {
  const now = args.now ?? ((): number => Date.now());
  const deadlineMs = args.deadlineMs ?? DEFAULT_DEADLINE_MS;
  const scanFn = args.scanImpl ?? listSubscriptionResources;

  // `timer` is declared out here so `finally` can clear it; everything that
  // could throw (including a caller-supplied onNarrate) stays inside the try
  // so this resolver can never reject and block the intake.
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    args.onNarrate?.("checking what's already in your subscription...");

    const scan = scanFn(args.subscriptionId);
    // C2 (defense-in-depth): the Promise.race below already attaches a rejection
    // handler to `scan`, so a late rejection is handled today. This explicit guard
    // keeps that property even if the race is ever replaced by another timeout
    // mechanism — a late-rejecting scan must never surface as an unhandledRejection
    // in the founder's terminal.
    void scan.catch(() => { /* swallowed: absence is the safe outcome */ });

    const timeout = new Promise<"timeout">((resolve) => {
      timer = setTimeout(() => { resolve("timeout"); }, deadlineMs);
    });

    const raced = await Promise.race([scan, timeout]);
    if (raced === "timeout" || !raced.ok) return {};
    const summary = summarizeInventory(raced.rows, args.installResourceGroup, now());
    return { note: renderInventoryNote(summary) };
  } catch {
    // Defensive backstop: listSubscriptionResources is documented never-throws, and
    // onNarrate is caller-supplied — degrade to absent rather than block the intake.
    return {};
  } finally {
    if (timer) clearTimeout(timer);
  }
}
